import React from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import round from 'lodash/round';
import { CusEcharts } from '@/components/AsyncComponent';
import type { ECOption } from '@/components/CusEcharts';
import {
  WORK_EXPERIENCE_1_3,
  WORK_EXPERIENCE_MAP,
} from '@/constants/job-title';
import { PRODUCTION_HOST } from '@/constants/router-path';
import { useClintMediaQuery } from '@/utils/hooks';
import { handleEchartsEvent } from '@/utils/common-utils';

export interface RecommendJobType {
  avg: number;
  min: number;
  max: number;
  currentCount: number;
  avgValueByExperienceDetails: Record<string, Record<string, number>>;
  currentCountByExperience: { name: string; value: number }[];
  name?: string;
  id?: string;
  titleClass?: string;
  jobTitleUrl?: string;
  avgMedian?: number | null;
  medianAvg?: number | null;
  minMedian?: number | null;
  maxMedian?: number | null;
}

const RecommendJob = (props: RecommendJobType) => {
  const {
    avg,
    min,
    max,
    avgValueByExperienceDetails,
    currentCountByExperience,
    currentCount,
    name,
    id,
    titleClass = '',
    jobTitleUrl = '',
    avgMedian,
    medianAvg,
    minMedian,
    maxMedian,
  } = props;
  const isMobile = useClintMediaQuery();
  const pieChartRef = React.useRef(null);
  const router = useRouter();
  const [showMedian, setShowMedian] = React.useState(false);

  const salaryByExperienceConfig = React.useMemo<ECOption>(() => {
    let totalCount = 0;
    const experienceKeys = Object.keys(avgValueByExperienceDetails || []);
    const hasDefaultKey = experienceKeys.includes(WORK_EXPERIENCE_1_3);
    currentCountByExperience.map((item) => (totalCount += item.value));
    const data: any[] = Object.entries(WORK_EXPERIENCE_MAP)
      .map(([key, item]) => {
        const salaryRange = Reflect.get(avgValueByExperienceDetails || {}, key);
        if (!salaryRange) {
          return;
        }

        return {
          name: item,
          value: currentCountByExperience.find((value) => value.name === key)
            ?.value,
          salary: `¥${round((salaryRange.min + salaryRange.max) / 2, -2)}`,
          label: {
            show:
              key === WORK_EXPERIENCE_1_3 ||
              (!hasDefaultKey && key === experienceKeys[0]),
          },
        };
      })
      .filter(Boolean);

    return {
      legend: {
        type: 'scroll',
        orient: 'horizontal',
        selectorPosition: 'end',
        bottom: isMobile ? '6%' : 0,
        left: 'center',
        padding: 0,
        itemGap: 12,
        icon: 'circle',
        itemHeight: 8,
        itemWidth: 16,
        selectedMode: false,
        textStyle: {
          fontWeight: 500,
          fontSize: 14,
          lineHeight: 18,
        },
        formatter: (name) => {
          const value = data.find((item) => item.name === name).value;
          return `\n${name}\n${round((value / currentCount) * 100)}%`;
        },
      },
      graphic: {
        type: 'text',
        left: 'center',
        top: isMobile ? '4%' : '41%',
        style: { text: '年限分布', fontSize: '.875rem', fontWeight: 600 },
      },
      color: ['#ff9f40', '#fc8452', '#5388d8', '#f4be37', '#0284c7', '#0d2535'],
      series: [
        {
          type: 'pie',
          radius: ['40%', '70%'],
          avoidLabelOverlap: true,
          label: {
            position: isMobile ? 'center' : 'outer',
            alignTo: 'none',
            bleedMargin: 5,
            fontSize: 14,
            lineHeight: 18,
            backgroundColor: 'transparent',
            textBorderWidth: 0,
            formatter: (params) => {
              const { name, salary } = params.data as Record<string, string>;
              return (
                '{icon' +
                params.dataIndex +
                '|●}' +
                `{name|${name}}` +
                '\n' +
                `{salary|${salary}}` +
                ''
              );
            },
            rich: {
              salary: { fontWeight: 'bold', fontSize: 14, lineHeight: 22 },
              icon0: {
                color: '#ff9f40',
                fontSize: 16,
                borderRadius: 4,
                padding: [0, 6, 0, 0],
              },
              icon1: {
                color: '#fc8452',
                fontSize: 16,
                borderRadius: 4,
                padding: [0, 6, 0, 0],
              },
              icon2: {
                color: '#5388d8',
                fontSize: 16,
                borderRadius: 4,
                padding: [0, 6, 0, 0],
              },
              icon3: {
                color: '#f4be37',
                fontSize: 16,
                borderRadius: 4,
                padding: [0, 6, 0, 0],
              },
              icon4: {
                color: '#0284c7',
                fontSize: 16,
                borderRadius: 4,
                padding: [0, 6, 0, 0],
              },
              icon5: {
                color: '#0d2535',
                fontSize: 16,
                borderRadius: 4,
                padding: [0, 6, 0, 0],
              },
            },
          },
          bottom: 40,
          labelLine: {
            show: true,
            showAbove: true,
          },
          emphasis: { disabled: true },
          cursor: 'default',
          data,
        },
      ],
    };
  }, [avgValueByExperienceDetails, currentCountByExperience, isMobile]);

  React.useEffect(() => {
    setShowMedian(window.location.host !== PRODUCTION_HOST);
  }, []);

  return (
    <div
      data-click={!!jobTitleUrl}
      className="flex flex-col rounded-lg text-base leading-6 p-5 pb-2 group data-[click=true]:cursor-pointer data-[click=true]:hover:bg-blue-50/80 tablet:flex-row tablet:px-10 tablet:py-7"
      onClick={() => jobTitleUrl && router.push(jobTitleUrl)}
    >
      <div className="flex-grow mb-6">
        {jobTitleUrl && (
          <Link
            href={jobTitleUrl}
            onClick={(e) => e.stopPropagation()}
            className={`text-xl font-semibold group-hover:underline group-hover:text-web-blue-500 ${titleClass}`}
          >
            {name}
          </Link>
        )}
        <div className="flex justify-between">
          <div>
            <p
              data-title={name && id}
              className="text-xl font-semibold data-[title=true]:text-sm data-[title=true]:leading-5.5 data-[title=true]:font-normal tablet:mt-11 tablet:data-[title=true]:mt-6"
            >
              平均月薪
            </p>
            <p
              data-title={name && id}
              className="text-2.5xl font-semibold mt-4 data-[title=true]:mt-2 tablet:text-3.25xl tablet:mt-6"
            >
              {`¥${round(avg, -2)}`}
            </p>
          </div>
          {showMedian && (
            <div className="flex text-base gap-2 grow justify-end tablet:mt-11">
              {avgMedian && (
                <div>
                  <p className="font-semibold">平均中位</p>
                  <p className="font-semibold mt-4">
                    {`¥${round(avgMedian, -2)}`}
                  </p>
                </div>
              )}
              {medianAvg && (
                <div>
                  <p className="font-semibold">中位平均</p>
                  <p className="font-semibold mt-4">
                    {`¥${round(medianAvg, -2)}`}
                  </p>
                </div>
              )}
              {minMedian && (
                <div>
                  <p className="font-semibold">最小中位</p>
                  <p className="font-semibold mt-4">
                    {`¥${round(minMedian, -2)}`}
                  </p>
                </div>
              )}
              {maxMedian && (
                <div>
                  <p className="font-semibold">最高中位</p>
                  <p className="font-semibold mt-4">
                    {`¥${round(maxMedian, -2)}`}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
        <p className="mt-2 text-sm leading-5.5">
          {`来自全网${currentCount.toLocaleString()}份数据`}
        </p>
        <div className="h-3.5 bg-blue-200 my-10 relative rounded-lg w-full min-w-full tablet:w-3/5 tablet:min-w-[300px]">
          <div className="absolute -left-0.5 -top-0.5">
            <div className="bg-blue-300 w-5 h-4 rounded-md" />
            <div className="mt-2 text-xs">{`最低¥${round(min, -2)}`}</div>
          </div>
          <div className="text-xs absolute bottom-[18px] left-1/2 -translate-x-1/2">
            {`¥${round(avg, -2)}`}
          </div>
          <div className="bg-web-blue-500 w-5 h-4 rounded-md absolute bottom-0 left-1/2 -translate-x-1/2" />
          <div className="bg-blue-300 w-5 h-4 rounded-md absolute -right-0.5 -top-0.5" />
          <div className="text-xs absolute right-0 top-[22px] tablet:translate-x-1/2">
            {`最高¥${round(max, -2)}`}
          </div>
        </div>
      </div>
      <div
        className="w-full h-[296px] tablet:w-[500px] [&_canvas]:cursor-default"
        onClick={(e) => e.stopPropagation()}
      >
        <CusEcharts
          config={salaryByExperienceConfig}
          chartRef={pieChartRef}
          handleEvent={handleEchartsEvent}
        />
      </div>
    </div>
  );
};

export default RecommendJob;
